import { add, money, toDb, ZERO } from '@miraki/money';
import type { Piece } from '@miraki/contracts';

/**
 * R-20 — the held list as PRODUCTS, not pieces.
 *
 * A retailer holding ten of the same ring does not want ten rows and ten Job Numbers; they want
 * "Ring X, size 7, yellow gold — 10". The pieces underneath are unchanged (invariant 3: one row per
 * physical piece, never a quantity on a row) — this is only how the list is READ. Selling 2 of 4
 * still marks two individual pieces sold, which `take()` picks.
 *
 * Pure, so it is tested without a browser. Money only through `@miraki/money`.
 */
export interface HeldGroup {
  key: string;
  name: string;
  goldColour: string | null;
  ringSize: string | null;
  variantCode: string | null;
  imageUrl: string | null;
  currency: string;
  /** One piece's wholesale value. Pieces of one variant share a price; the first one's is shown. */
  unitPrice: string;
  /** Oldest received first, so a sale takes the piece that has been held longest. */
  pieces: Piece[];
}

/** Product + variant. Description alone would merge two sizes of one ring (see `zPiece.variantCode`). */
export const groupKey = (p: Piece) =>
  [p.description, p.variantCode ?? '', p.goldColour ?? '', p.ringSize ?? '', p.currency, p.wholesaleValue].join('|');

export function groupHeld(pieces: readonly Piece[]): HeldGroup[] {
  const by = new Map<string, HeldGroup>();
  for (const p of pieces) {
    const key = groupKey(p);
    const g = by.get(key);
    if (g) { g.pieces.push(p); if (!g.imageUrl && p.imageUrl) g.imageUrl = p.imageUrl; continue; }
    by.set(key, {
      key, name: p.productName ?? p.description, goldColour: p.goldColour, ringSize: p.ringSize,
      variantCode: p.variantCode, imageUrl: p.imageUrl, currency: p.currency,
      unitPrice: p.wholesaleValue, pieces: [p],
    });
  }
  const out = [...by.values()];
  for (const g of out) {
    g.pieces.sort((a, b) => (a.receivedAt ?? '').localeCompare(b.receivedAt ?? '') || a.id.localeCompare(b.id));
  }
  return out.sort((a, b) => a.name.localeCompare(b.name) || a.key.localeCompare(b.key));
}

/** The pieces a sale of `n` of this group takes: the n oldest, never more than the group holds. */
export const take = (g: HeldGroup, n: number): Piece[] => g.pieces.slice(0, Math.max(0, Math.min(n, g.pieces.length)));

/** What `n` of a group are worth, as a decimal string. */
export const worth = (g: HeldGroup, n: number): string =>
  toDb(take(g, n).reduce((t, p) => add(t, money(p.wholesaleValue)), ZERO));
